import {
  AltiusRes,
  BulkReviewSort,
  IBulkBatch,
  IBulkBatchReview,
  IBulkFileStatusEvent,
  IBulkSaveResponse
} from "../../types";
import { BulkUploadAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IBulkUploadActions {
  setBulkBatchesAction: (batches: IBulkBatch[]) => void;
  applyBulkFileStatusAction: (event: IBulkFileStatusEvent) => void;
  markBulkBatchAwaitingReviewAction: (batchId: string) => void;
  removeBulkBatchAction: (batchId: string) => void;
  clearBulkUploadAction: () => void;

  getBulkUploadBatchesAction: () => Promise<IBulkBatch[]>;
  getBulkUploadBatchReviewAction: (batchId: string, sort?: BulkReviewSort) => Promise<IBulkBatchReview>;
  saveBulkUploadBatchAction: (batchId: string) => Promise<IBulkSaveResponse>;
  dismissBulkUploadBatchAction: (batchId: string) => Promise<void>;
  cancelBulkUploadBatchAction: (batchId: string) => Promise<void>;
}

export class BulkUploadActions extends AsyncActions implements IBulkUploadActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public setBulkBatchesAction: IBulkUploadActions["setBulkBatchesAction"] = (batches) => {
    this.dispatch({ type: BulkUploadAction.SetBatches, payload: batches });
  };

  public applyBulkFileStatusAction: IBulkUploadActions["applyBulkFileStatusAction"] = (event) => {
    this.dispatch({ type: BulkUploadAction.ApplyFileStatus, payload: event });
  };

  public markBulkBatchAwaitingReviewAction: IBulkUploadActions["markBulkBatchAwaitingReviewAction"] = (batchId) => {
    this.dispatch({ type: BulkUploadAction.MarkAwaitingReview, payload: batchId });
  };

  public removeBulkBatchAction: IBulkUploadActions["removeBulkBatchAction"] = (batchId) => {
    this.dispatch({ type: BulkUploadAction.RemoveBatch, payload: batchId });
  };

  public clearBulkUploadAction: IBulkUploadActions["clearBulkUploadAction"] = () => {
    this.dispatch({ type: BulkUploadAction.Clear, payload: null });
  };

  public getBulkUploadBatchesAction: IBulkUploadActions["getBulkUploadBatchesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IBulkBatch[]>>({
      url: "/api/v0.0.3/bulk-upload/batches"
    });
    return data;
  };

  public getBulkUploadBatchReviewAction: IBulkUploadActions["getBulkUploadBatchReviewAction"] = async (
    batchId,
    sort
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IBulkBatchReview>>({
      url: `/api/v0.0.3/bulk-upload/batches/${batchId}/review`,
      ...(sort && { params: { sort } })
    });
    return data;
  };

  public saveBulkUploadBatchAction: IBulkUploadActions["saveBulkUploadBatchAction"] = async (batchId) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IBulkSaveResponse>>({
      url: `/api/v0.0.3/bulk-upload/batches/${batchId}/save`
    });
    return data;
  };

  public dismissBulkUploadBatchAction: IBulkUploadActions["dismissBulkUploadBatchAction"] = async (batchId) => {
    await this.api.post({ url: `/api/v0.0.3/bulk-upload/batches/${batchId}/dismiss` });
  };

  public cancelBulkUploadBatchAction: IBulkUploadActions["cancelBulkUploadBatchAction"] = async (batchId) => {
    await this.api.post({ url: `/api/v0.0.3/bulk-upload/batches/${batchId}/cancel` });
  };
}
